/**
 * Files larger than this open the download SideDrawer (SAS URL wizard)
 * instead of triggering an immediate browser download.
 */
export const LARGE_FILE_DRAWER_THRESHOLD_BYTES = 10 * 1024 ** 3;

/**
 * Fixed catalog path for the large-file download instructions PDF.
 * Deploy prerequisite: this blob must exist under tdis-data-bronze before
 * the instructions link can mint a SAS URL.
 */
export const DOWNLOAD_INSTRUCTIONS_BLOB_PATH =
  "rbfs/approved-models/InstructionsForAzCopyDownload.pdf";

/** SAS lifetime for the instructions PDF (≤10 GB tier). */
export const DOWNLOAD_INSTRUCTIONS_SAS_MINUTES = 120;

/**
 * True when `value` is an absolute http(s) URL.
 * An empty or relative href resolves to the DMQT app and opens the map.
 * @param {unknown} value
 * @returns {boolean}
 */
export function isAbsoluteHttpUrl(value) {
  return typeof value === "string" && /^https?:\/\//i.test(value.trim());
}

const GB = 1024 ** 3;

/** Default SAS lifetime when catalog size is missing/invalid. */
export const SAS_DURATION_DEFAULT_MINUTES = 120;

/**
 * SAS lifetime tiers by catalog file size (bytes).
 * Token Factory service_scopes.max_duration_minutes must allow the max tier.
 *
 * | Catalog file size     | SAS expiry      |
 * | --------------------- | --------------- |
 * | ≤ 10 GB               | 120 min (2 h)   |
 * | > 10 GB, ≤ 100 GB     | 480 min (8 h)   |
 * | > 100 GB, ≤ 200 GB    | 960 min (16 h)  |
 * | > 200 GB, ≤ 500 GB    | 1440 min (24 h) |
 * | > 500 GB              | 2880 min (48 h) |
 */
export const SAS_DURATION_TIERS = [
  { maxBytes: 10 * GB, minutes: 120 },
  { maxBytes: 100 * GB, minutes: 480 },
  { maxBytes: 200 * GB, minutes: 960 },
  { maxBytes: 500 * GB, minutes: 1440 },
  { maxBytes: Infinity, minutes: 2880 },
];

/**
 * Resolve SAS duration_minutes from catalog file size.
 * Missing/invalid size → SAS_DURATION_DEFAULT_MINUTES.
 * @param {number|string|null|undefined} sizeInBytes
 * @returns {number}
 */
export function sasDurationMinutesForFileSize(sizeInBytes) {
  const n = Number(sizeInBytes);
  if (!Number.isFinite(n) || n <= 0) return SAS_DURATION_DEFAULT_MINUTES;
  const tier = SAS_DURATION_TIERS.find((t) => n <= t.maxBytes);
  return tier ? tier.minutes : 2880;
}
